import { inject, Injectable } from '@angular/core';
import {
  catchError,
  from,
  map,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { COMMO_SHARED_SUPABASE_CLIENT } from '../commo-shared-data-access.config';
import {
  USER_AVATAR_MAX_BYTES,
  USER_AVATARS_BUCKET,
  userAvatarStoragePath,
} from '../constants/user-avatars';

@Injectable({ providedIn: 'root' })
export class UserAvatarsService {
  private readonly supabase = inject(COMMO_SHARED_SUPABASE_CLIENT);

  /**
   * DB の path / updated_at を先に更新してから JPEG を upsert する。path は常に auth.uid()。
   * Storage 失敗時:
   * - 初回（hadExisting=false）→ DB をクリア
   * - 差し替え（hadExisting=true）→ DB はそのまま（旧ファイルは残る）
   * UI は両方成功後にだけ再取得する前提。
   */
  uploadAvatar(blob: Blob, hadExisting = false) {
    if (blob.size > USER_AVATAR_MAX_BYTES) {
      return throwError(() => new Error('Avatar file is too large.'));
    }
    return this.withAuthedUserId((userId) => {
      const path = userAvatarStoragePath(userId);
      return from(
        this.supabase.rpc('set_profile_avatar', {
          p_clear: false,
        }),
      ).pipe(
        switchMap(({ error: rpcError }) => {
          if (rpcError) {
            return throwError(() => new Error(rpcError.message));
          }
          return from(
            this.supabase.storage.from(USER_AVATARS_BUCKET).upload(path, blob, {
              upsert: true,
              contentType: 'image/jpeg',
              cacheControl: '3600',
            }),
          ).pipe(
            switchMap(({ error: uploadError }) => {
              if (!uploadError) return of(path);
              const fail = throwError(() => new Error(uploadError.message));
              if (hadExisting) return fail;
              return this.clearAvatarMeta().pipe(
                catchError(() => of(undefined)),
                switchMap(() => fail),
              );
            }),
          );
        }),
      );
    });
  }

  /**
   * Storage を先に削除してから DB をクリアする。
   * 削除失敗時は DB を触らない。RPC 失敗時は一度だけリトライ。
   */
  clearAvatar() {
    return this.withAuthedUserId((userId) => {
      const path = userAvatarStoragePath(userId);
      return from(
        this.supabase.storage.from(USER_AVATARS_BUCKET).remove([path]),
      ).pipe(
        switchMap(({ error: removeError }) => {
          if (removeError) {
            return throwError(() => new Error(removeError.message));
          }
          return this.clearAvatarMeta();
        }),
      );
    });
  }

  private clearAvatarMeta() {
    const call = () =>
      from(
        this.supabase.rpc('set_profile_avatar', {
          p_clear: true,
        }),
      );

    return call().pipe(
      switchMap(({ error }) => {
        if (!error) return of(undefined);
        return call().pipe(
          map(({ error: retryError }) => {
            if (retryError) throw new Error(retryError.message);
            return undefined;
          }),
        );
      }),
    );
  }

  private withAuthedUserId<T>(
    run: (userId: string) => Observable<T>,
  ): Observable<T> {
    return from(this.supabase.auth.getSession()).pipe(
      switchMap(({ data, error }) => {
        const userId = data.session?.user?.id;
        if (error || !userId) {
          return throwError(
            () => new Error(error?.message ?? 'Not authenticated'),
          );
        }
        return run(userId);
      }),
    );
  }
}
